import { call, put, takeLatest, select } from 'redux-saga/effects';

import { getProductScope } from '@discover-frontend/utils';
import {
  GET_EXPLORE_ITEMS_REQUEST,
  GET_EXPLORE_ITEMS_SUCCESS,
  GET_EXPLORE_ITEMS_FAILURE,
  LOAD_MORE_RESULTS_REQUEST,
  LOAD_MORE_RESULTS_SUCCESS,
  LOAD_MORE_RESULTS_FAILURE,
  GET_EXPLORE_CONVERSATION_ITEMS_REQUEST,
  GET_EXPLORE_CONVERSATION_ITEMS_SUCCESS,
  GET_EXPLORE_CONVERSATION_ITEMS_FAILURE,
  GET_SAVED_QUERIES_REQUEST,
  GET_SAVED_QUERIES_SUCCESS,
  GET_SAVED_QUERIES_FAILURE,
  LOAD_MORE_SAVED_QUERIES_REQUEST,
  LOAD_MORE_SAVED_QUERIES_FAILURE,
  LOAD_MORE_SAVED_QUERIES_SUCCESS,
} from '../constants/action-types';
import * as engineApi from '../services/engineApi';
import * as api from '../services/repositoryApi';
import { getSources } from '../utils/AuthHelperMethods';
import sagaFetch from './SagaFetch';
import { getSavedQueries } from '../utils/misc';

const getQueryString = (params, sources) => {
  const queryStringArr = [];
  if (!params) {
    queryStringArr.push(`sources=${sources}`);
    queryStringArr.push(`scope=${getProductScope()}`);
    return `?${queryStringArr.join('&')}`;
  }

  if (params.pagination) {
    queryStringArr.push(`page=${params.pagination.page}`);
    queryStringArr.push(`limit=${params.pagination.limit}`);
  }

  if (params.variableTypes) {
    queryStringArr.push(`variable_types=${params.variableTypes}`);
  }

  if (params.q) {
    const encoded = encodeURIComponent(params.q.replace("'", ''));
    queryStringArr.push(`q=${encoded}`);
  }

  if (params.categoryId) {
    queryStringArr.push(`category_id=${params.categoryId}`);
  }

  if (params.sources && params.sources !== 'all') {
    queryStringArr.push(`sources=${params.sources}`);
  } else {
    queryStringArr.push(`sources=${sources}`);
  }

  queryStringArr.push(`scope=${getProductScope()}`);
  return `?${queryStringArr.join('&')}`;
};

function* fetchSavedQueries(action) {
  const resultsStateParams = yield select((state) => state.exploreItems.params);
  const folders = yield select((state) => state.repository.folders);
  const dataSource = yield select((state) => state.queryBuilder.dataSource);

  const folderId =
    action.id && action.id.includes('folder_')
      ? action.id.replace('folder_', '')
      : null;
  const { response, error } = yield sagaFetch(api.fetchRepositoryApi, {
    mode: 'saved-queries',
    pagination: resultsStateParams.pagination,
    queryType: resultsStateParams.queryType,
    q: resultsStateParams.q,
    parent: folderId,
    source: dataSource,
  });

  if (response) {
    return yield put({
      type: GET_SAVED_QUERIES_SUCCESS,
      response: getSavedQueries(response.data, folders),
      total_count: response.total_count,
    });
  }
  yield put({
    type: GET_SAVED_QUERIES_FAILURE,
    message: error.message,
  });
}

function* fetchLoadMoreSavedQueries(action) {
  const resultsStateParams = yield select((state) => state.exploreItems.params);
  const folders = yield select((state) => state.repository.folders);
  const dataSource = yield select((state) => state.queryBuilder.dataSource);
  const folderId =
    action.id && action.id.includes('folder_')
      ? action.id.replace('folder_', '')
      : null;

  const { response, error } = yield sagaFetch(api.fetchRepositoryApi, {
    mode: 'saved-queries',
    pagination: resultsStateParams.pagination,
    queryType: resultsStateParams.queryType,
    q: resultsStateParams.q,
    parent: folderId,
    source: dataSource,
  });

  if (response) {
    return yield put({
      type: LOAD_MORE_SAVED_QUERIES_SUCCESS,
      response: getSavedQueries(response.data, folders),
    });
  }
  yield put({
    type: LOAD_MORE_SAVED_QUERIES_FAILURE,
    message: error.message,
  });
}

function* fetchExploreItems() {
  const resultsStateParams = yield select((state) => state.exploreItems.params);
  const dataSource = yield select((state) => state.queryBuilder.dataSource);

  const sources = getSources(dataSource);
  const queryString = getQueryString(resultsStateParams, sources);

  const { response, error } = yield call(
    engineApi.fetchExploreItemsInTaxonomyApi,
    queryString
  );
  if (response) {
    yield put({
      type: GET_EXPLORE_ITEMS_SUCCESS,
      response: response.data,
      total_count: response.total_count,
    });
  } else {
    yield put({ type: GET_EXPLORE_ITEMS_FAILURE, message: error });
  }
}

function* fetchLoadMoreResults() {
  const resultsStateParams = yield select((state) => state.exploreItems.params);
  const dataSource = yield select((state) => state.queryBuilder.dataSource);

  const sources = getSources(dataSource);

  const queryString = getQueryString(resultsStateParams, sources);

  const { response, error } = yield call(
    engineApi.fetchExploreItemsInTaxonomyApi,
    queryString
  );
  const dataResponse = response.data.slice(0, response.data.length - 1);

  if (response) {
    yield put({ type: LOAD_MORE_RESULTS_SUCCESS, data: dataResponse });
  } else {
    yield put({ type: LOAD_MORE_RESULTS_FAILURE, error });
  }
}

function* fetchExploreConversationItems(action) {
  const resultsStateParams = yield select((state) => state.exploreItems.params);

  const { response, error } = yield call(api.fetchRepositoryApi, {
    mode: 'conversation-taxonomy',
    q: resultsStateParams.q,
    pagination: resultsStateParams.pagination,
  });
  if (response) {
    yield put({
      type: GET_EXPLORE_CONVERSATION_ITEMS_SUCCESS,
      response: response.data,
      total_count: response.data.length,
    });
  } else {
    yield put({ type: GET_EXPLORE_CONVERSATION_ITEMS_FAILURE, message: error });
  }
}

export function* exploreItemsActionWatcher() {
  yield takeLatest(GET_EXPLORE_ITEMS_REQUEST, fetchExploreItems);
  yield takeLatest(LOAD_MORE_RESULTS_REQUEST, fetchLoadMoreResults);
  yield takeLatest(
    GET_EXPLORE_CONVERSATION_ITEMS_REQUEST,
    fetchExploreConversationItems
  );
  yield takeLatest(GET_SAVED_QUERIES_REQUEST, fetchSavedQueries);
  yield takeLatest(LOAD_MORE_SAVED_QUERIES_REQUEST, fetchLoadMoreSavedQueries);
}
